// Data-backed import-tax estimator.
//
// Each observation is ONE real past assessment from the ICUMS/UniPass used-vehicle duty
// search: the vehicle, the CIF value (national currency) and the total tax assessed. We
// compare the *effective rate* (total_tax / cif_value), which is unit-free as long as both
// figures are in the same currency (they are, in the UniPass table: CIF NCY and Total Tax).
//
// This module never invents data: with too few comparable observations it returns null and
// callers must fall back to a clearly-labelled rough allowance.

export interface DutyObservation {
  make: string;
  model: string;
  model_year: number;
  engine_cc?: number | null;
  cif_value: number;
  total_tax: number;
  observed_on?: string;
}

export type Confidence = "limited" | "fair" | "good";

export interface DutyEstimate {
  rate: number; // median effective rate, e.g. 0.31 = 31% of CIF
  low: number; // lowest comparable rate
  high: number; // highest comparable rate
  sampleSize: number;
  confidence: Confidence;
  basis: "similar-year" | "all-years";
  latestObservation?: string | undefined;
}

export const MIN_SAMPLE = 3;
const YEAR_WINDOW = 2;

export function effectiveRate(
  o: Pick<DutyObservation, "cif_value" | "total_tax">,
): number {
  return o.total_tax / o.cif_value;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

function median(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? (sorted[mid] ?? 0)
    : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function confidenceFor(n: number): Confidence {
  return n >= 10 ? "good" : n >= 5 ? "fair" : "limited";
}

export function estimateDutyRate(
  observations: DutyObservation[],
  query: { make: string; model: string; year?: number },
): DutyEstimate | null {
  const sameModel = observations.filter(
    (o) =>
      norm(o.make) === norm(query.make) &&
      norm(o.model) === norm(query.model) &&
      Number.isFinite(o.cif_value) &&
      o.cif_value > 0 &&
      Number.isFinite(o.total_tax) &&
      o.total_tax >= 0 &&
      effectiveRate(o) <= 2, // discard obvious data-entry errors
  );

  let pool = sameModel;
  let basis: DutyEstimate["basis"] = "all-years";
  if (query.year != null) {
    const near = sameModel.filter(
      (o) => Math.abs(o.model_year - query.year!) <= YEAR_WINDOW,
    );
    if (near.length >= MIN_SAMPLE) {
      pool = near;
      basis = "similar-year";
    }
  }
  if (pool.length < MIN_SAMPLE) return null;

  const rates = pool.map(effectiveRate).sort((a, b) => a - b);
  const dates = pool
    .map((o) => o.observed_on)
    .filter((d): d is string => !!d)
    .sort();
  return {
    rate: median(rates),
    low: rates[0] ?? 0,
    high: rates[rates.length - 1] ?? 0,
    sampleSize: pool.length,
    confidence: confidenceFor(pool.length),
    basis,
    latestObservation: dates[dates.length - 1],
  };
}

// ---------- Import: UniPass table paste (tab-separated) or simple CSV ----------

export interface ParsedObservation extends DutyObservation {
  fuel_type: string | null;
  currency: string;
  observed_on: string;
  trim_level?: string | null;
  origin_code?: string | null;
  hs_code?: string | null;
  exchange_rate?: number | null;
  receipt_date?: string | null;
}
export interface CsvError {
  line: number;
  message: string;
}

const REQUIRED = [
  "make",
  "model",
  "model_year",
  "cif_value",
  "total_tax",
] as const;

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const toNum = (s: string | undefined) =>
  s === undefined || s.trim() === "" ? NaN : Number(s.replace(/[,\s]/g, ""));

export function parseObservationCsv(
  text: string,
  today = new Date().toISOString().slice(0, 10),
): {
  valid: ParsedObservation[];
  errors: CsvError[];
} {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const valid: ParsedObservation[] = [];
  const errors: CsvError[] = [];
  if (lines.length === 0)
    return { valid, errors: [{ line: 0, message: "Nothing to import." }] };

  const header = splitCsvLine(lines[0] ?? "").map((h) => h.toLowerCase());
  const missing = REQUIRED.filter((c) => !header.includes(c));
  if (missing.length) {
    return {
      valid,
      errors: [
        {
          line: 1,
          message: `Missing required column(s): ${missing.join(", ")}`,
        },
      ],
    };
  }
  const col = (cells: string[], name: string) => cells[header.indexOf(name)];

  for (let i = 1; i < lines.length; i++) {
    const lineNo = i + 1;
    const cells = splitCsvLine(lines[i] ?? "");
    const make = col(cells, "make")?.trim();
    const model = col(cells, "model")?.trim();
    const year = toNum(col(cells, "model_year"));
    const cif = toNum(col(cells, "cif_value"));
    const tax = toNum(col(cells, "total_tax"));
    const ccRaw = header.includes("engine_cc")
      ? toNum(col(cells, "engine_cc"))
      : NaN;
    const currency =
      (header.includes("currency")
        ? col(cells, "currency")
        : ""
      )?.toUpperCase() || "USD";
    const observed =
      (header.includes("observed_on") ? col(cells, "observed_on") : "") ||
      today;
    const fuel =
      (header.includes("fuel_type") ? col(cells, "fuel_type") : "")?.trim() ||
      null;

    if (!make || !model) {
      errors.push({ line: lineNo, message: "make and model are required" });
      continue;
    }
    if (
      !Number.isInteger(year) ||
      year < 1980 ||
      year > new Date().getFullYear() + 1
    ) {
      errors.push({
        line: lineNo,
        message: `invalid model_year "${col(cells, "model_year")}"`,
      });
      continue;
    }
    if (!Number.isFinite(cif) || cif <= 0) {
      errors.push({
        line: lineNo,
        message: "cif_value must be a positive number",
      });
      continue;
    }
    if (!Number.isFinite(tax) || tax < 0) {
      errors.push({ line: lineNo, message: "total_tax must be zero or more" });
      continue;
    }
    if (tax / cif > 2) {
      errors.push({
        line: lineNo,
        message:
          "total_tax is over 200% of CIF — check the columns are not swapped",
      });
      continue;
    }
    if (!/^[A-Z]{3}$/.test(currency)) {
      errors.push({ line: lineNo, message: `invalid currency "${currency}"` });
      continue;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(observed)) {
      errors.push({ line: lineNo, message: "observed_on must be YYYY-MM-DD" });
      continue;
    }

    valid.push({
      make,
      model,
      model_year: year,
      engine_cc: Number.isFinite(ccRaw) && ccRaw > 0 ? Math.round(ccRaw) : null,
      fuel_type: fuel,
      cif_value: cif,
      total_tax: tax,
      currency,
      observed_on: observed,
    });
  }
  return { valid, errors };
}

// ---------- UniPass table paste ----------
// Columns exactly as shown by the site's result list (note their spelling "Excange Rate"):
// No. | Trim Level | Year of Manufacture | Make | Model | HDV | Currency | Origin Code |
// HS Code | Excange Rate | Receipt Date | Assessment Date | CIF NCY | Total Tax
// Selecting the table in a browser and copying yields tab-separated cells.

const UNIPASS_ORDER = [
  "no",
  "trim",
  "year",
  "make",
  "model",
  "hdv",
  "currency",
  "origin",
  "hs",
  "rate",
  "receipt",
  "assessment",
  "cif",
  "tax",
] as const;
type UnipassCol = (typeof UNIPASS_ORDER)[number];

const HEADER_ALIASES: Record<string, UnipassCol> = {
  no: "no",
  trimlevel: "trim",
  yearofmanufacture: "year",
  make: "make",
  model: "model",
  hdv: "hdv",
  currency: "currency",
  origincode: "origin",
  hscode: "hs",
  excangerate: "rate",
  exchangerate: "rate",
  receiptdate: "receipt",
  assessmentdate: "assessment",
  cifncy: "cif",
  totaltax: "tax",
};
const key = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");

export function looksLikeUnipass(text: string): boolean {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? "";
  const cells = first.split("\t").map(key);
  if (cells.includes("assessmentdate") && cells.includes("cifncy")) return true; // header row
  // headerless rows: 13-14 tab-separated cells with a DD/MM/YYYY-style date
  return (
    first.split("\t").length >= 13 &&
    /\d{2}[/-]\d{2}[/-]\d{4}|\d{4}-\d{2}-\d{2}/.test(first)
  );
}

/** DD/MM/YYYY or YYYY-MM-DD (optionally followed by a time) -> YYYY-MM-DD, else null. */
export function toIsoDate(raw: string | undefined): string | null {
  if (!raw) return null;
  const t = raw.trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let y: number, mo: number, d: number;
  if (m) {
    y = +(m[1] ?? 0);
    mo = +(m[2] ?? 0);
    d = +(m[3] ?? 0);
  } else {
    m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (!m) return null;
    d = +(m[1] ?? 0);
    mo = +(m[2] ?? 0);
    y = +(m[3] ?? 0);
  }
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
  )
    return null; // e.g. 31/02
  return dt.toISOString().slice(0, 10);
}

export function parseUnipassPaste(text: string): {
  valid: ParsedObservation[];
  errors: CsvError[];
} {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const valid: ParsedObservation[] = [];
  const errors: CsvError[] = [];
  if (lines.length === 0)
    return { valid, errors: [{ line: 0, message: "Nothing to import." }] };

  // Column map: from the header if present, otherwise the fixed site order.
  const map: Partial<Record<UnipassCol, number>> = {};
  let start = 0;
  const headerCells = (lines[0] ?? "").split("\t");
  const fromHeader = headerCells.map((h) => HEADER_ALIASES[key(h)]);
  if (fromHeader.includes("assessment") && fromHeader.includes("cif")) {
    fromHeader.forEach((c, i) => {
      if (c) map[c] = i;
    });
    start = 1;
    const missing = (
      ["make", "model", "year", "assessment", "cif", "tax"] as UnipassCol[]
    ).filter((c) => map[c] === undefined);
    if (missing.length)
      return {
        valid,
        errors: [
          {
            line: 1,
            message: `Header is missing column(s): ${missing.join(", ")}`,
          },
        ],
      };
  } else {
    // headerless: allow with or without the leading "No." column
    const offset = headerCells.length >= 14 ? 0 : 1;
    UNIPASS_ORDER.forEach((c, i) => {
      if (i - offset >= 0) map[c] = i - offset;
    });
  }
  const get = (cells: string[], c: UnipassCol) =>
    map[c] === undefined ? undefined : cells[map[c] as number]?.trim();
  const num = (s: string | undefined) =>
    s === undefined || s === "" ? NaN : Number(s.replace(/[,\s]/g, ""));

  for (let i = start; i < lines.length; i++) {
    const lineNo = i + 1;
    const rawLine = lines[i] ?? "";
    const cells = rawLine.split("\t");
    if (/no data found/i.test(rawLine)) {
      errors.push({
        line: lineNo,
        message: 'The site returned "No data found" for this search',
      });
      continue;
    }

    const make = get(cells, "make");
    const model = get(cells, "model");
    const year = num(get(cells, "year"));
    const cif = num(get(cells, "cif"));
    const tax = num(get(cells, "tax"));
    const assessed = toIsoDate(get(cells, "assessment"));
    const rate = num(get(cells, "rate"));
    const receipt = toIsoDate(get(cells, "receipt"));

    if (!make || !model) {
      errors.push({ line: lineNo, message: "make and model are required" });
      continue;
    }
    if (
      !Number.isInteger(year) ||
      year < 1980 ||
      year > new Date().getFullYear() + 1
    ) {
      errors.push({
        line: lineNo,
        message: `invalid year "${get(cells, "year")}"`,
      });
      continue;
    }
    if (!Number.isFinite(cif) || cif <= 0) {
      errors.push({
        line: lineNo,
        message: "CIF NCY must be a positive number",
      });
      continue;
    }
    if (!Number.isFinite(tax) || tax < 0) {
      errors.push({ line: lineNo, message: "Total Tax must be zero or more" });
      continue;
    }
    if (tax / cif > 2) {
      errors.push({
        line: lineNo,
        message:
          "Total Tax is over 200% of CIF: check the columns lined up correctly",
      });
      continue;
    }
    if (!assessed) {
      errors.push({
        line: lineNo,
        message: `invalid Assessment Date "${get(cells, "assessment")}"`,
      });
      continue;
    }

    valid.push({
      make,
      model,
      model_year: year,
      engine_cc: null,
      fuel_type: null,
      cif_value: cif,
      total_tax: tax,
      currency: "GHS", // "CIF NCY" and "Total Tax" are national currency
      observed_on: assessed,
      trim_level: get(cells, "trim") || null,
      origin_code: get(cells, "origin") || null,
      hs_code: get(cells, "hs") || null,
      exchange_rate: Number.isFinite(rate) && rate > 0 ? rate : null,
      receipt_date: receipt,
    });
  }
  return { valid, errors };
}

/** Accepts either a UniPass table paste or the simple CSV format. */
export function parseObservationInput(text: string) {
  return looksLikeUnipass(text)
    ? parseUnipassPaste(text)
    : parseObservationCsv(text);
}
